import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRightFromSquare, Save, Download, ArrowLeft } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import { useApi } from '../contexts/ApiContext';
import { useAIOperation } from '../hooks/useAIOperation';
import { ProviderRegistry } from '../services/providerRegistry';
import { ReplicateProvider } from '../services/providers/ReplicateProvider';
import ImagePicker from '../components/ImagePicker';
import ProviderToggle from '../components/ProviderToggle';
import ImageCompare from '../components/ImageCompare';
import JobStatus, { CostBadge } from '../components/JobStatus';
import type { MedaImage } from '../types/image';

type Pad = { top: number; right: number; bottom: number; left: number };

const MAX_PAD = 1024;

function clampPad(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(MAX_PAD, Math.round(v)));
}

async function composeExtended(
  src: Blob,
  pad: Pad,
): Promise<{ image: Blob; mask: Blob }> {
  const bitmap = await createImageBitmap(src);
  const W = bitmap.width + pad.left + pad.right;
  const H = bitmap.height + pad.top + pad.bottom;

  // Extended image: paste original at (left, top) on a solid mid-gray background
  const imgCanvas = new OffscreenCanvas(W, H);
  const ictx = imgCanvas.getContext('2d')!;
  ictx.fillStyle = '#808080';
  ictx.fillRect(0, 0, W, H);
  ictx.drawImage(bitmap, pad.left, pad.top);

  // Mask: white (fill) everywhere EXCEPT the original image region (black = keep)
  const maskCanvas = new OffscreenCanvas(W, H);
  const mctx = maskCanvas.getContext('2d')!;
  mctx.fillStyle = 'white';
  mctx.fillRect(0, 0, W, H);
  mctx.fillStyle = 'black';
  mctx.fillRect(pad.left, pad.top, bitmap.width, bitmap.height);

  bitmap.close();

  const [imageBlob, maskBlob] = await Promise.all([
    imgCanvas.convertToBlob({ type: 'image/png' }),
    maskCanvas.convertToBlob({ type: 'image/png' }),
  ]);
  return { image: imageBlob, mask: maskBlob };
}

function drawCheckerboard(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const size = 16;
  const light = '#2a2927';
  const dark = '#1c1b1a';
  for (let y = 0; y < h; y += size) {
    for (let x = 0; x < w; x += size) {
      const even = ((x / size) + (y / size)) % 2 === 0;
      ctx.fillStyle = even ? light : dark;
      ctx.fillRect(x, y, size, size);
    }
  }
}

interface PadInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
}

function PadInput({ label, value, onChange }: PadInputProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-[0.15em] w-12" style={{ color: 'var(--sage-text-muted)' }}>
        {label}
      </span>
      <input
        type="range"
        min={0}
        max={MAX_PAD}
        step={8}
        value={value}
        onChange={e => onChange(clampPad(Number(e.target.value)))}
        className="flex-1"
      />
      <input
        type="number"
        min={0}
        max={MAX_PAD}
        step={8}
        value={value}
        onChange={e => onChange(clampPad(Number(e.target.value)))}
        className="w-16 px-2 py-1 rounded text-xs text-right outline-none"
        style={{
          backgroundColor: 'var(--sage-bg)',
          color: 'var(--sage-text)',
          border: '1px solid var(--sage-white-10)',
        }}
      />
    </div>
  );
}

export default function ExtendPage() {
  const navigate = useNavigate();
  const { addImage } = useLibrary();
  const { tier, setTier, apiKeys } = useApi();
  const [opState, opActions] = useAIOperation();

  const [selectedImage, setSelectedImage] = useState<MedaImage | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [pad, setPad] = useState<Pad>({ top: 0, right: 0, bottom: 0, left: 0 });
  const [prompt, setPrompt] = useState('');
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);

  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const registry = useMemo(() => new ProviderRegistry({
    huggingfaceKey: apiKeys.huggingface,
    replicateKey: apiKeys.replicate,
    preferredTier: tier,
  }), [apiKeys.huggingface, apiKeys.replicate, tier]);

  const provider = useMemo(() => registry.getBestProvider('outpaint'), [registry]);
  const providers = useMemo(() => registry.getProvidersForOp('outpaint'), [registry]);
  const hasFree = providers.some(p => p.info.tier === 'free');
  const hasPaid = providers.some(p => p.info.tier === 'paid');

  const estimatedCost = useMemo(() => {
    if (!provider) return 0;
    if (provider.info.tier === 'free') return 0;
    if (provider instanceof ReplicateProvider) return provider.getEstimatedCost('outpaint');
    return 0.05;
  }, [provider]);

  useEffect(() => () => {
    if (originalUrl) URL.revokeObjectURL(originalUrl);
  }, [originalUrl]);

  useEffect(() => () => {
    if (bitmap) bitmap.close();
  }, [bitmap]);

  const handleSelect = useCallback(async (image: MedaImage) => {
    setSelectedImage(image);
    setPad({ top: 0, right: 0, bottom: 0, left: 0 });
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setOriginalUrl(URL.createObjectURL(image.blob));
    opActions.reset();
    try {
      const bmp = await createImageBitmap(image.blob);
      setBitmap(prev => {
        if (prev) prev.close();
        return bmp;
      });
    } catch {
      setBitmap(prev => {
        if (prev) prev.close();
        return null;
      });
    }
  }, [originalUrl, opActions]);

  const handleUploadFile = useCallback(async (file: File) => {
    const img = await addImage(file, file.name);
    await handleSelect(img);
  }, [addImage, handleSelect]);

  // Render live preview canvas
  useEffect(() => {
    if (!selectedImage || !bitmap || !previewCanvasRef.current) return;
    const W = bitmap.width + pad.left + pad.right;
    const H = bitmap.height + pad.top + pad.bottom;
    const canvas = previewCanvasRef.current;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Checkerboard behind everything (shows through in padding areas)
    drawCheckerboard(ctx, W, H);
    // Draw original image in its original location
    ctx.drawImage(bitmap, pad.left, pad.top);
    // Dashed border for the extended bounds
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.lineWidth = Math.max(2, Math.round(Math.min(W, H) / 400));
    ctx.setLineDash([12, 8]);
    ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
    // Border around the original image area
    ctx.strokeStyle = 'rgba(212, 165, 116, 0.9)';
    ctx.setLineDash([]);
    ctx.strokeRect(pad.left + 0.5, pad.top + 0.5, bitmap.width - 1, bitmap.height - 1);
    ctx.restore();
  }, [selectedImage, bitmap, pad]);

  const outW = selectedImage ? selectedImage.width + pad.left + pad.right : 0;
  const outH = selectedImage ? selectedImage.height + pad.top + pad.bottom : 0;
  const totalPad = pad.top + pad.right + pad.bottom + pad.left;

  const handleRun = useCallback(async () => {
    if (!selectedImage || !provider || totalPad <= 0) return;
    const { image: extendedBlob, mask: maskBlob } = await composeExtended(selectedImage.blob, pad);
    await opActions.run(provider, 'outpaint', extendedBlob, {
      mask: maskBlob,
      prompt: prompt.trim() || 'extend this image naturally, seamless continuation',
    });
  }, [selectedImage, provider, totalPad, pad, prompt, opActions]);

  const handleSave = useCallback(async () => {
    if (!opState.resultBlob || !selectedImage) return;
    const label = `${selectedImage.name} (extend ${outW}x${outH})`;
    await addImage(
      opState.resultBlob,
      label,
      'generated',
      selectedImage.id,
      'outpaint',
    );
    navigate('/library');
  }, [opState.resultBlob, selectedImage, outW, outH, addImage, navigate]);

  const handleDownload = useCallback(() => {
    if (!opState.resultUrl || !selectedImage) return;
    const a = document.createElement('a');
    a.href = opState.resultUrl;
    a.download = `${selectedImage.name}_extend_${outW}x${outH}.png`;
    a.click();
  }, [opState.resultUrl, selectedImage, outW, outH]);

  const noProvider = !provider;
  const needsKey = noProvider && !apiKeys.replicate;
  const running = opState.status === 'submitting' || opState.status === 'processing';
  const canRun = !!(selectedImage && provider && totalPad > 0);

  return (
    <div className="flex flex-col h-full sage-animate-fade-in">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
      >
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg" style={{ color: 'var(--sage-text-muted)' }}>
          <ArrowLeft size={18} />
        </button>
        <ArrowUpRightFromSquare size={18} style={{ color: 'var(--sage-primary)' }} />
        <h1 className="text-sm font-medium flex-1">Extend</h1>
        <ProviderToggle tier={tier} onToggle={setTier} freeAvailable={hasFree} paidAvailable={hasPaid} />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left panel — controls */}
        <div
          className="w-72 p-4 border-r overflow-y-auto flex flex-col gap-5"
          style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Select Image
            </p>
            <ImagePicker
              selectedId={selectedImage?.id ?? null}
              onSelect={handleSelect}
              onUpload={handleUploadFile}
            />
          </div>

          {selectedImage && (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
                  Padding (px)
                </p>
                <div className="flex flex-col gap-2">
                  <PadInput label="Top" value={pad.top} onChange={v => setPad(p => ({ ...p, top: v }))} />
                  <PadInput label="Right" value={pad.right} onChange={v => setPad(p => ({ ...p, right: v }))} />
                  <PadInput label="Bottom" value={pad.bottom} onChange={v => setPad(p => ({ ...p, bottom: v }))} />
                  <PadInput label="Left" value={pad.left} onChange={v => setPad(p => ({ ...p, left: v }))} />
                </div>
                <p className="text-[10px] mt-2" style={{ color: 'var(--sage-text-muted)' }}>
                  {selectedImage.width}x{selectedImage.height} -&gt; {outW}x{outH}
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Style Prompt (optional)
                </p>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="e.g. lush forest extending outward"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-xs resize-none outline-none"
                  style={{
                    backgroundColor: 'var(--sage-bg)',
                    color: 'var(--sage-text)',
                    border: '1px solid var(--sage-white-10)',
                  }}
                />
                <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Empty = seamless natural continuation.
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Provider
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
                    {provider?.info.name || 'None configured'}
                  </span>
                  <CostBadge cost={estimatedCost} tier={provider?.info.tier || 'paid'} />
                </div>
                {!hasFree && (
                  <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
                    Outpaint requires a paid provider (Replicate).
                  </p>
                )}
              </div>

              {needsKey ? (
                <div
                  className="p-3 rounded-lg text-xs"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--sage-accent) 10%, var(--sage-bg))', color: 'var(--sage-accent)' }}
                >
                  Add a Replicate API key in Settings to enable outpaint.
                  <button onClick={() => navigate('/settings')} className="underline ml-1">Open Settings</button>
                </div>
              ) : (
                <button
                  onClick={handleRun}
                  disabled={!canRun || running}
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                >
                  {running ? 'Processing...' : totalPad > 0 ? 'Extend' : 'Set padding to continue'}
                </button>
              )}

              <JobStatus
                status={opState.status}
                progress={opState.progress}
                error={opState.error}
                onRetry={handleRun}
              />

              {opState.status === 'succeeded' && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                  >
                    <Save size={14} /> Save to Library
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs"
                    style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
                  >
                    <Download size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Main area — preview / comparison */}
        <div className="flex-1 flex items-center justify-center p-6 min-w-0 overflow-auto">
          {!selectedImage ? (
            <div className="text-center">
              <ArrowUpRightFromSquare size={48} style={{ color: 'var(--sage-white-10)' }} className="mx-auto mb-3" />
              <p className="text-sm" style={{ color: 'var(--sage-text-muted)' }}>
                Select an image to extend
              </p>
            </div>
          ) : opState.status === 'succeeded' && opState.resultUrl && originalUrl ? (
            <div className="w-full max-w-3xl">
              <ImageCompare
                beforeUrl={originalUrl}
                afterUrl={opState.resultUrl}
                beforeLabel="Original"
                afterLabel="Extended"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center max-w-full">
              <canvas
                ref={previewCanvasRef}
                className="rounded-xl shadow-lg"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
              <p className="text-[11px] text-center mt-3" style={{ color: 'var(--sage-text-muted)' }}>
                {totalPad > 0
                  ? `Checkered area will be outpainted (${outW}x${outH})`
                  : 'Drag padding sliders to expand the canvas'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
